<script setup lang="ts">
/*
 * The cookie consent toggles and their Save button. The only editor of the
 * stored consent (utils/consent.ts): the cookie policy page shows it as a
 * card, the cookie banner embeds it `compact`. Essential is always on.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToastStore } from '../../stores/toast';
import { defaultConsent, loadConsent, saveConsent, type CookieConsent } from '../../utils/consent';
import ConsentToggle from './ConsentToggle.vue';

defineProps<{ compact?: boolean }>();
const emit = defineEmits<{ (e: 'saved'): void }>();

const { t, locale } = useI18n();
const toast = useToastStore();

const consent = ref<CookieConsent>(defaultConsent());
// Essential is not user-editable; the lock row just displays it.
const essentialOn = ref(true);

onMounted(() => {
  consent.value = loadConsent() ?? defaultConsent();
});

const lastSaved = computed(() => {
  const at = consent.value.decidedAt;
  if (!at) return null;
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(locale.value, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
});

function save() {
  consent.value.decidedAt = new Date().toISOString();
  saveConsent(consent.value);
  toast.push({ title: t('legal.cookies.preferences.saved') });
  emit('saved');
}
</script>

<template>
  <div class="flex flex-col gap-5" :class="compact ? '' : 'surface p-6 md:p-8'">
    <ConsentToggle v-model="essentialOn" locked :label="$t('legal.cookies.preferences.essential.label')" :description="$t('legal.cookies.preferences.essential.description')" />
    <ConsentToggle v-model="consent.analytics" :label="$t('legal.cookies.preferences.analytics.label')" :description="$t('legal.cookies.preferences.analytics.description')" />
    <ConsentToggle v-model="consent.marketing" :label="$t('legal.cookies.preferences.marketing.label')" :description="$t('legal.cookies.preferences.marketing.description')" />

    <div class="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p v-if="!compact" class="text-[13px] text-text-faint">
        {{ lastSaved ? $t('legal.cookies.preferences.lastSaved', { date: lastSaved }) : $t('legal.cookies.preferences.notSaved') }}
      </p>
      <!-- In the banner "Accept all" is the one amber action, so Save is secondary there. -->
      <button type="button" class="btn-sm" :class="compact ? 'btn-secondary w-full' : 'btn-primary'" @click="save">{{ $t('legal.cookies.preferences.saveButton') }}</button>
    </div>
  </div>
</template>
